<template>
  <!-- justify-center -->
  <div class="flex flex-col h-[100%] justify-start pt-[100px] lg:pt-[20%]">
    <!-- intro -->
    <div class="flex flex-col gap-[32px] mb-[32px]">
      <p class="text-lg text-grays-900 font-regular">שלב 1 מתוך 2</p>
      <div class="flex flex-row items-center gap-[8px]">
        <div
          class="flex justify-center items-center h-[40px] w-[40px] bg-complementary-600 text-complementary-900 rounded-full"
        >
          <span class="material-symbols-rounded">account_circle</span>
        </div>
        <div class="flex flex-col">
          <h1 class="text-primary-700 text-3xl font-bold">השלמת ההרשמה</h1>
          <p class="text-purple_semantic text-base font-regular">
            פרופיל משפחתי
          </p>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-[46px] pb-[156px]">
      <!-- User details forms -->
      <div class="flex flex-col w-full gap-[16px]">
        <p class="text-grays-800 text-base font-medium">פרטים אישיים</p>
        <div class="flex flex-col w-full gap-[14px]">
          <div class="lg:flex flex-row w-full gap-[20px]">
            <input-default
              :error="v$.lname.$error"
              :errorMessage="v$.lname.$errors[0]?.$message"
              :label="'שלום למשפחת-'"
              :model-value="lname"
              @update:model-value="setLname"
            />
            <input-default
              :error="v$.fname.$error"
              :errorMessage="v$.fname.$errors[0]?.$message"
              :label="'שם פרטי'"
              :model-value="fname"
              @update:model-value="setFname"
            />
            <input-default
              :error="v$.idNumber.$error"
              :errorMessage="v$.idNumber.$errors[0]?.$message"
              :label="'תעודת זהות'"
              :model-value="idNumber"
              @update:model-value="setIdNumber"
            />
          </div>
          <div class="lg:flex flex-row w-full gap-[20px]">
            <input-default
              :error="v$.city.$error"
              :errorMessage="v$.city.$errors[0]?.$message"
              :label="'עיר'"
              :model-value="city"
              @update:model-value="setCity"
            />
            <input-default
              :error="v$.street.$error"
              :errorMessage="v$.street.$errors[0]?.$message"
              :label="'רחוב'"
              :model-value="street"
              @update:model-value="setStreet"
            />
          </div>
          <error-block v-if="requestError" :error-message="errorMessage" />
        </div>
      </div>
      <!--Add phones -->
      <div class="flex flex-col w-full gap-[16px]">
        <p class="text-grays-800 text-base font-medium">יצירת קשר</p>
        <div class="w-full gap-[20px] grid lg:grid-cols-3">
          <div
            v-for="(phoneNumber, index) in allPhoneNumbers"
            :key="index"
            class="w-fit flex gap-4"
          >
            <input-default
              :disabled="!editMode"
              :error="
                (index === 0
                  ? !this.v$.allPhoneNumbers.firstPhoneRequire.$response
                  : false) ||
                (v$.allPhoneNumbers.$each.$response.$errors
                  ? !!v$.allPhoneNumbers.$each.$response.$errors[index].tel
                      .length
                  : false)
              "
              :errorMessage="
                index === 0 &&
                !this.v$.allPhoneNumbers.firstPhoneRequire.$response
                  ? this.v$.allPhoneNumbers.firstPhoneRequire.$message.toString()
                  : v$.allPhoneNumbers.$each.$response.$errors &&
                    v$.allPhoneNumbers.$each.$response.$errors[index].tel.length
                  ? v$.allPhoneNumbers.$each.$response.$errors[index].tel[0]
                      .$message
                  : ''
              "
              :label="'טלפון ' + (index + 1)"
              :model-value="phoneNumber.tel"
              @update:model-value="updatePhoneNumber(index, $event)"
            />
            <button
              v-if="allPhoneNumbers.length > 1"
              class="mt-8 h-fit"
              @click="deletePhone(index)"
            >
              <span class="material-symbols-rounded text-lg text-grays-800"
                >delete</span
              >
            </button>
          </div>
          <button-text class="mt-[25px]" @click="addNewPhone">
            <span class="material-symbols-rounded text-lg">add</span>
            טלפון נוסף
          </button-text>
        </div>
      </div>

      <!--Add reminders -->
      <div class="flex flex-col gap-22 min-w-auto xl:min-w-[1050px]">
        <button
          class="flex flex-row items-center gap-24 w-fit"
          @click="handleRemindersDropdown"
        >
          <div class="flex flex-row items-center gap-8">
            <h3 class="text-primary-700 text-xl font-medium">ניהול תזכורות</h3>
            <p class="text-grays-700 text-sm font-regular">אופציונאלי</p>
          </div>
          <span class="material-symbols-rounded text-2xl text-primary-700">{{
            showReminders ? "expand_less" : "expand_more"
          }}</span>
        </button>

        <!-- Reminders section -->
        <section v-if="showReminders" :class="{ scrollDown: showReminders }">
          <div class="flex flex-row gap-[35px] items-end">
            <div class="flex flex-row gap-9 items-end">
              <div class="flex flex-row gap-20">
                <!-- whenToRemind -->
                <div class="flex flex-col gap-4">
                  <p class="text-grays-900 font-medium text-lg">
                    מתי לשלוח תזכורת?
                  </p>
                  <div class="lg:flex flex-row gap-4 relative">
                    <check-box
                      v-for="remind in whenToRemind"
                      :key="remind.type"
                      :disabled="!editMode"
                      :model-value="remind.value"
                      @update:model-value="
                        updateWhenToRemind(remind.type, $event)
                      "
                    >
                      {{ remind.display }}
                    </check-box>
                    <div
                      v-if="phoneRequiredMessage"
                      class="text-sm text-error absolute bottom-[-40px] right-2"
                    >
                      {{ phoneRequiredMessage }}
                      <span class="text-error material-symbols-rounded text-sm"
                        >error</span
                      >
                    </div>
                  </div>
                </div>
                <!-- whereToRemind -->
                <div
                  v-if="atLeastOneWhenToRemindSelected()"
                  class="flex flex-col gap-4 mt-0.5"
                >
                  <div class="flex flex-row items-center gap-2">
                    <p class="text-grays-900 font-medium text-lg">
                      איפה תרצה לקבל תזכורת?
                    </p>
                    <span class="text-grays-700 text-sm"
                      >מינימום בחירה 1 - חובה</span
                    >
                  </div>
                  <div class="flex flex-col gap-2">
                    <!-- Checkboxes in a row -->
                    <div class="flex flex-row items-center gap-[90px]">
                      <check-box
                        v-for="remind in whereToRemind.slice().reverse()"
                        :key="remind.type"
                        :disabled="!editMode"
                        :model-value="remind.value"
                        @update:model-value="
                          updateWhereToRemind(remind.type, $event)
                        "
                      >
                        {{ remind.display }}
                      </check-box>
                    </div>
                    <div class="flex flex-row gap-2">
                      <div
                        class="min-w-[150px] max-w-[150px] flex items-center"
                      >
                        <p class="text-grays-500 text-sm font-regular">
                          {{ profileData.email }}
                        </p>
                      </div>
                      <!-- Dropdown menu -->
                      <div v-if="editMode">
                        <dropdown-reminders
                          :disabled="
                            !whereToRemind.find(
                              (remind) => remind.type === 'phone'
                            )?.value
                          "
                          :phones="allPhoneNumbers"
                          :remind-all-phones="allPhoneNumbersToRemind"
                          :selected-phones="selectedPhoneNumbers"
                          @update-phones-reminds="updatePhonesReminds"
                          @update-all-phone-numbers-to-remind="
                            updateAllPhoneNumbersToRemind
                          "
                        />
                      </div>
                      <div
                        v-else-if="
                          whereToRemind.find(
                            (remind) => remind.type === 'phone'
                          )?.value
                        "
                        class="text-grays-500 text-sm font-regular"
                      >
                        {{
                          allPhoneNumbersToRemind
                            ? "All numbers"
                            : selectedPhoneNumbers.join(", ")
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Next Step Btn -->
    <div
      class="flex flex-row-reverse justify-end w-full py-[38px] pr-4 lg:pr-[87px] pl-4 lg:pl-0 fixed right-0 bottom-0 items-center gap-[16px] bg-white"
    >
      <div class="flex flex-row gap-[8px]">
        <p class="text-grays-900 text-base font-regular">לחזרה להרשמה מהירה</p>
        <button
          class="text-primary-700 text-base font-bold underline hover:text-primary-800 focus:text-primary-900 active:text-primary-900 disabled:text-grays-600"
          @click="handleCompleteRegistrationLater"
        >
          לחזרה להרשמה מהירה
        </button>
      </div>
      <button-primary @click="handleNextStep">
        {{ isLoading ? "טוען נתונים..." : "ממשיכים" }}
      </button-primary>
    </div>
  </div>
</template>

<script>
import useValidate from "@vuelidate/core";
import { userValidation } from "@/validation/User";
import axiosInstance from "../../helpers/axios.js";
import { mapGetters, mapMutations } from "vuex";
import { toRaw } from "vue";
import ButtonText from "@/components/Global/ButtonText.vue";
import InputDefault from "@/components/Global/InputDefault.vue";
import CheckBox from "@/components/Global/CheckBox.vue";
import DropdownReminders from "@/components/Profile/DropdownReminders.vue";
import ErrorBlock from "@/components/Global/ErrorBlock.vue";
import ButtonPrimary from "@/components/Global/ButtonPrimary.vue";

export default {
  name: "complete-user",
  components: {
    ButtonPrimary,
    ErrorBlock,
    DropdownReminders,
    CheckBox,
    InputDefault,
    ButtonText,
  },
  data() {
    return {
      editMode: true,
      v$: useValidate(),
      selectedPhoneNumbers: [],
      allPhoneNumbersToRemind: false,
      phoneRequiredMessage: "",
      showReminders: false,
      validateNewPhones: false,
      isLoading: false,
      requestError: false,
      profileData: {},
      errorMessage: "",
    };
  },
  validations() {
    return {
      idNumber: userValidation.idNumber,
      fname: userValidation.fname,
      lname: userValidation.lname,
      street: userValidation.street,
      city: userValidation.city,
      allPhoneNumbers: userValidation.allPhoneNumbers,
    };
  },
  methods: {
    ...mapMutations({
      setIdNumber: "user/setIdNumber",
      setFname: "user/setFname",
      setLname: "user/setLname",
      setStreet: "user/setStreet",
      setCity: "user/setCity",
      setAllPhoneNumbers: "user/setAllPhoneNumbers",
      setWhenToRemind: "user/setWhenToRemind",
      setWhereToRemind: "user/setWhereToRemind",
      updateAllPhoneNumbers: "user/updateAllPhoneNumbers",
      setPhoneRemind: "user/setPhoneRemind",
      addPhone: "user/addPhone",
      deleteAllNullPhoneNumbers: "user/deleteAllNullPhoneNumbers",
    }),
    completeData() {
      this.setWhenToRemind({
        type: "beforeClosingSale",
        value: this.profileData.remind_before_closing_sale,
      });
      this.setWhenToRemind({
        type: "dayOfPickup",
        value: this.profileData.remind_on_day_of_pickup,
      });
      this.setWhenToRemind({
        type: "openingNewSale",
        value: this.profileData.remind_on_opening_new_sale,
      });

      this.updateAllPhoneNumbers(
        this.profileData.phones.map((phone) => ({
          tel: phone.tel,
          no_msg: phone.no_msg,
        }))
      );

      this.selectedPhoneNumbers = this.profileData.phones
        .filter((phone) => phone.no_msg === 0)
        .map((phone) => phone.tel);
      if (this.selectedPhoneNumbers.length > 0) {
        this.setWhereToRemind({ type: "phone", value: 1 });
      }
      this.setWhereToRemind({
        type: "email",
        value: this.profileData.email_notifications,
      });
    },
    atLeastOneWhenToRemindSelected() {
      return this.whenToRemind.some((remind) => remind.value);
    },
    validateReminders() {
      this.phoneRequiredMessage = "";

      const isWhenToRemindSelected = this.atLeastOneWhenToRemindSelected();
      const hasWhereToRemind = this.whereToRemind.some(
        (remind) => remind.value
      );
      const hasPhoneReminder = this.whereToRemind.some(
        (remind) => remind.type === "phone" && remind.value
      );

      if (isWhenToRemindSelected && hasWhereToRemind) {
        if (hasPhoneReminder && !this.selectedPhoneNumbers.length) {
          this.phoneRequiredMessage = "Choose at least one phone number";
          return false;
        }
        return true;
      }

      if (isWhenToRemindSelected && !hasWhereToRemind) {
        this.phoneRequiredMessage = "Choose where to remind";
        return false;
      }

      return true;
    },
    deletePhone(index) {
      this.allPhoneNumbers.splice(index, 1);
    },
    async getProfileData() {
      try {
        localStorage.setItem("token", this.token);
        const response = await axiosInstance.get("/new_profile");
        localStorage.removeItem("token");
        this.profileData = response.data;
        this.$store.dispatch(
          "findStation/updateAddress",
          this.profileData.site.street
        );
      } catch (error) {
        console.error(error);
      }
    },
    getUserEmail() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (Object.keys(user).length === 0) {
        return;
      }
      return user.email;
    },
    updatePhoneNumber(index, value) {
      this.setAllPhoneNumbers({ index, value });
    },
    addNewPhone() {
      this.addPhone({ tel: "", no_msg: 0 });
    },
    handleRemindersDropdown() {
      this.showReminders = !this.showReminders;
    },
    preventUncheck(checkboxName, array) {
      const checkbox = array.find((item) => item.type === checkboxName);
      return this.checkedCount === 1 && checkbox.type;
    },
    handleCompleteRegistrationLater() {
      this.$router.push("/send-email-verification");
    },
    updateAllPhoneNumbersToRemind(newValue) {
      this.updateAllPhoneNumbers(
        this.allPhoneNumbers.map((phone) => {
          return { ...phone, no_msg: newValue };
        })
      );
      this.selectedPhoneNumbers = !newValue
        ? this.allPhoneNumbers.map((phone) => phone.tel)
        : [];
    },
    updatePhonesReminds(data) {
      const { tel } = data;
      if (this.selectedPhoneNumbers.indexOf(tel) !== -1) {
        this.selectedPhoneNumbers = this.selectedPhoneNumbers.filter(
          (phone) => +phone !== +tel
        );
      } else {
        this.selectedPhoneNumbers.push(tel);
      }
    },
    updateWhereToRemind(type, value) {
      this.setWhereToRemind({ type, value });
      if (!this.whereToRemind.find((remind) => remind.value === 1)) {
        this.setWhereToRemind({ type, value: 1 });
      }
    },
    updateWhenToRemind(type, value) {
      this.setWhenToRemind({ type, value });
    },
    async handleSaveReminders() {
      const valid = this.validateReminders();
      try {
        if (valid) {
          this.phoneRequiredMessage = "";
          const whenToRemind = this.whenToRemind
            .map((remind) => {
              if (remind.value === 1) {
                if (remind.type === "beforeClosingSale") {
                  return "remind_before_closing_sale";
                } else if (remind.type === "openingNewSale") {
                  return "remind_on_opening_new_sale";
                } else if (remind.type === "dayOfPickup") {
                  return "remind_on_day_of_pickup";
                }
              }
            })
            .filter((remind) => remind !== undefined);
          const whereToRemind = [
            {
              type: "email",
              value: +this.whereToRemind.some(
                (remind) => remind.type === "email" && remind.value === 1
              ),
            },
            ...(this.whereToRemind.find((item) => item.type === "phone")
              ? this.allPhoneNumbersToRemind
                ? [{ type: "phone", value: "all" }]
                : this.selectedPhoneNumbers.map((phone) => ({
                    type: "phone",
                    value: +phone,
                  }))
              : []),
          ];

          await axiosInstance.put("/clients/notifications", {
            whereToRemind,
            whenToRemind,
          });
          if (!this.whereToRemind.find((item) => item.type === "phone")) {
            this.selectedPhoneNumbers = [];
            this.allPhoneNumbers.map((phone, index) => {
              this.setPhoneRemind({ index, value: 1 });
            });
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    async handleNextStep() {
      this.v$.$validate();
      this.validateNewPhones = true;

      if (!this.v$.$error) {
        this.deleteAllNullPhoneNumbers();
        this.isLoading = true;
        try {
          const modifiedPhoneNumbers = this.allPhoneNumbersToRemind
            ? this.allPhoneNumbers.map((phoneNumber) => ({
                phone: phoneNumber.tel,
                no_msg: 1,
              }))
            : this.allPhoneNumbers.map((item) => ({
                phone: item.tel,
                no_msg: item.no_msg,
              }));

          const dataForm = {
            tzl: this.idNumber,
            fname: this.fname,
            lname: this.lname,
            street: this.street,
            city: this.city,
            allPhoneNumbers: modifiedPhoneNumbers,
          };
          localStorage.setItem("token", this.token);
          await axiosInstance.put("/clients", dataForm);
          await this.handleSaveReminders();
          localStorage.removeItem("token");
          // Reset request error flag.
          this.requestError = false;
          // Redirect user to the next step (credit card).
          this.$emit("data-updated");
        } catch (e) {
          this.errorMessage = e.response?.data?.message || "";
          this.requestError = true;
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  mounted() {
    this.getProfileData();
  },
  computed: {
    ...mapGetters({
      lname: "user/getLname",
      fname: "user/getFname",
      idNumber: "user/getIdNumber",
      city: "user/getCity",
      street: "user/getStreet",
      token: "user/getToken",
      whenToRemind: "user/getWhenToRemind",
      whereToRemind: "user/getWhereToRemind",
      allPhoneNumbers: "user/getAllPhoneNumbers",
    }),
    checkedCount() {
      return this.whereToRemind.filter((item) => item.value).length;
    },
  },
  watch: {
    profileData: {
      deep: true,
      handler(updatedData) {
        const rawData = toRaw(updatedData).phones.map((item) => item.tel);
        this.setFname(updatedData.fname);
        this.setLname(updatedData.lname);
        this.setIdNumber(updatedData.tzl);
        this.setStreet(updatedData.street);
        this.setCity(updatedData.city);
        rawData.forEach((value, index) => {
          this.setAllPhoneNumbers({ index, value });
        });
      },
      profileData() {
        this.completeData();
      },
    },
  },
};
</script>

<style>
.scrollDown {
  animation: dropdown 0.5s ease forwards;
  transform-origin: top;
}

@keyframes dropdown {
  from {
    opacity: 0;
    transform: translateY(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
}

.scrollLeft {
  animation: dropLeft 0.5s ease forwards;
  transform-origin: top;
}

@keyframes dropLeft {
  from {
    opacity: 0;
    transform: translateX(50%);
  }
  to {
    opacity: 1;
    transform: translateX(0%);
  }
}
</style>